import { useChapterHeadings } from "./useChapterHeadings";
import {
  computed,
  ref,
  unref,
  watch,
  type MaybeRef,
  type ComputedRef,
} from "vue";
import {
  fromMaybe,
  isJust,
  isNothing,
  mapMaybe,
  matchMaybe,
  type Maybe,
} from "../lib/maybe";
import { countManuscriptWords } from "../lib/wordCount";
import { joinProjectPath } from "../lib/runtime";
import { readProjectFile } from "../lib/tauri/core";
import { scenePath } from "../lib/zwConfig";
import type { EditorKind } from "../lib/tauri/core";
import type {
  ActiveSceneInfo,
  TrashPreview,
} from "../stores/useEditorSessionStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";

const WORDS_PER_MINUTE = 200;

/** Accepts either a Pinia session store or the legacy ref-based context. */
type ChapterStatsSession = {
  trashPreview: MaybeRef<Maybe<TrashPreview>>;
  doc: MaybeRef<string>;
  kind: MaybeRef<EditorKind>;
  rootPath: MaybeRef<Maybe<string>>;
  activePath: MaybeRef<Maybe<string>>;
  activeSceneInfo: MaybeRef<Maybe<ActiveSceneInfo>>;
  activeBasename: MaybeRef<Maybe<string>>;
};

export function useChapterStats(session: ChapterStatsSession): {
  chapterLabel: ComputedRef<Maybe<string>>;
  wordLabel: ComputedRef<string>;
  readingLabel: ComputedRef<string>;
  canShowHistory: ComputedRef<boolean>;
} {
  const siblingWords = ref<Record<string, number>>({});

  const activeInfo = computed((): Maybe<ActiveSceneInfo> =>
    unref(session.activeSceneInfo),
  );

  const openBasename = computed((): Maybe<string> =>
    unref(session.activeBasename),
  );

  const chapterLabel = computed((): Maybe<string> =>
    mapMaybe(activeInfo.value, (info) => info.chapter.name),
  );

  const chapterScenePaths = computed((): string[] =>
    matchMaybe(activeInfo.value, {
      nothing: () => [],
      just: (info) =>
        info.chapter.scenes.map((scene) => scenePath(info.folder, scene.file)),
    }),
  );

  async function loadSiblingWords(
    root: string,
    paths: string[],
    openPath: Maybe<string>,
  ): Promise<void> {
    const next: Record<string, number> = {};
    await Promise.all(
      paths.map(async (path) => {
        if (isJust(openPath) && path === openPath.value) return;
        try {
          const raw = await readProjectFile(joinProjectPath(root, path));
          if (fromMaybe(unref(session.rootPath)) !== root) return;
          next[path] = countManuscriptWords(raw);
        } catch {
          // Leave missing; open-scene count still shows.
        }
      }),
    );
    if (fromMaybe(unref(session.rootPath)) !== root) return;
    siblingWords.value = next;
  }

  watch(
    [
      () => fromMaybe(unref(session.rootPath)) ?? "",
      () => chapterScenePaths.value.join("\0"),
      () => fromMaybe(openBasename.value) ?? "",
      () => isJust(unref(session.trashPreview)),
    ],
    () => {
      if (isJust(unref(session.trashPreview))) {
        siblingWords.value = {};
        return;
      }
      const rootM = unref(session.rootPath);
      const paths = chapterScenePaths.value;
      if (isNothing(rootM) || paths.length <= 1) {
        siblingWords.value = {};
        return;
      }
      void loadSiblingWords(rootM.value, paths, openBasename.value);
    },
    { immediate: true },
  );

  const openSceneWords = computed(() => {
    const preview = unref(session.trashPreview);
    const text = isJust(preview) ? preview.value.content : unref(session.doc);
    return countManuscriptWords(text);
  });

  const wordCount = computed(() => {
    if (isJust(unref(session.trashPreview))) return openSceneWords.value;
    const paths = chapterScenePaths.value;
    if (paths.length <= 1) return openSceneWords.value;
    const openPath = openBasename.value;
    let total = openSceneWords.value;
    for (const path of paths) {
      if (isJust(openPath) && path === openPath.value) continue;
      const cached = siblingWords.value[path];
      if (typeof cached === "number") total += cached;
    }
    return total;
  });

  const wordLabel = computed(() => {
    const n = wordCount.value;
    return `${n.toLocaleString()} ${n === 1 ? "word" : "words"}`;
  });

  const readingLabel = computed(() => {
    const words = wordCount.value;
    if (words <= 0) return "0 min read";
    const minutes = Math.max(1, Math.round(words / WORDS_PER_MINUTE));
    return `${minutes} min read`;
  });

  const canShowHistory = computed(
    () =>
      unref(session.kind) === "project" &&
      isJust(unref(session.rootPath)) &&
      (isJust(unref(session.activePath)) ||
        isJust(unref(session.trashPreview))),
  );

  return { chapterLabel, wordLabel, readingLabel, canShowHistory };
}

/** Status-bar chapter metrics from the editor session store. */
export function useChapterStatsFromSession(): {
  chapterLabel: ComputedRef<Maybe<string>>;
  wordLabel: ComputedRef<string>;
  readingLabel: ComputedRef<string>;
  canShowHistory: ComputedRef<boolean>;
} {
  const session = useEditorSessionStore();
  const { chapterLabel } = useChapterHeadings();
  return {
    ...useChapterStats(session),
    chapterLabel: computed(() =>
      mapMaybe(session.activeSceneInfo, (info) => chapterLabel(info.folder)),
    ),
  };
}
